import {createSelector} from "reselect";
import * as authSelectors from "@atg-shared/auth/domain/authSelectors";
import {AUTH_REQUIRED_PAGES} from "@atg-sport-shared/sportsbook-utils-constants";
import {getSportRoute} from "@atg-sport-shared/sportsbook-data-access-route";
import type {KambiState, OddsFormat} from "@atg-sport-shared/sportsbook-types-kambi";
import type {
    GlobalState,
    GlobalSportState,
} from "@atg-sport-shared/sportsbook-types-root";
import * as userSelectors from "@atg-global-shared/user/userSelectors";
import {getIsKambiDisposing, getIsKambiLoading} from "./kambi.client.selectors";

const kambiSelector = (state: GlobalSportState): KambiState => state.sportsbook.kambi;

export const isKambiLoggedIn: (state: GlobalSportState) => boolean = createSelector(
    kambiSelector,
    (kambi: KambiState) => kambi.isLoggedIn,
);

export const getBetCost: (state: GlobalSportState) => number = createSelector(
    kambiSelector,
    (kambi: KambiState) => kambi.betCost,
);

export const isKambiLoginPending: (state: GlobalSportState) => boolean = createSelector(
    kambiSelector,
    (kambi: KambiState) => kambi.isLoginPending,
);

export const hasUnexpectedError: (state: GlobalSportState) => boolean = createSelector(
    kambiSelector,
    (kambi: KambiState): boolean => kambi.loginFailedReason !== undefined,
);

export const getHighlightsLoaded: (state: GlobalSportState) => boolean = createSelector(
    kambiSelector,
    ({isHighlightsLoaded}) => isHighlightsLoaded,
);

export const getKambiLoaded: (state: GlobalSportState) => boolean = createSelector(
    kambiSelector,
    (kambi) => kambi.kambiLoaded,
);

export const showKambiPlaceholder: (state: GlobalState) => boolean = createSelector(
    isKambiLoggedIn,
    hasUnexpectedError,
    getSportRoute,
    // @ts-ignore
    authSelectors.isNormalLogin,
    (kambiLoggedIn, unexpectedError, route, isNormalLogin) =>
        Boolean(
            route?.page &&
                AUTH_REQUIRED_PAGES.includes(route.page) &&
                isNormalLogin &&
                !unexpectedError &&
                !kambiLoggedIn,
        ),
);

export const getOddsFormat: (state: GlobalSportState) => OddsFormat = createSelector(
    kambiSelector,
    (kambi: KambiState): OddsFormat => kambi.oddsFormat,
);

export const isKambiReadyToLogin: (state: GlobalState) => boolean = createSelector(
    authSelectors.isNormalLogin,
    // @ts-ignore
    isKambiLoggedIn,
    userSelectors.getUsername,
    isKambiLoginPending,
    getIsKambiLoading,
    getIsKambiDisposing,
    (isLoggedIn, kambiLoggedIn, username, kambiLoginPending, isLoading, isDisposing) =>
        Boolean(
            isLoggedIn &&
                username &&
                !kambiLoggedIn &&
                !kambiLoginPending &&
                !isLoading &&
                !isDisposing,
        ),
);
